/* ============================================================================
 * Nirlon Gift Flow — PAGE 2: Gift Selection engine (self-contained, vanilla JS)
 * Spec: Gift_Flow_SPEC.md §5 + Option-B grouping.
 *
 * Responsibilities
 *   1. Session guard: require nirlon_gift_session cookie, else redirect to "/".
 *      Read nirlon_gift_tier -> maxItems.
 *   2. Build Option-B cards from the #gift-data JSON island:
 *        - color swatches (native Color option OR split-family color_group)
 *        - size chips, then a length selector (Inseam/Pants length) if present
 *        - a variant is selectable ONLY if eligible === true (gift metafield);
 *          non-eligible options render greyed/disabled.
 *   3. Strikethrough price -> $0. Running "X of TIER selected" counter,
 *      live "Shipping: $X.XX", limit enforcement, remove/swap.
 *   4. Checkout: /cart/clear.js  ->  /cart/add.js (gift variants)  ->
 *      add GIFT-SHIP variant matching the count  ->  redirect to
 *      /discount/GIFTSHIPFREE?redirect=/checkout  (single free-shipping code;
 *      the free GIFTS come from an AUTOMATIC Buy-X-Get-Y discount that needs no
 *      code. One-step checkout no longer auto-applies multiple comma-codes.)
 *
 * No theme dependency. No external libs.
 * ==========================================================================*/
(function () {
  "use strict";

  var SHIP_PRICE = { 1: "9.99", 2: "14.99", 3: "17.99", 4: "21.99", 5: "24.99" };

  /* ---------- tiny helpers ---------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function el(tag, attrs, kids) {
    var n = document.createElement(tag);
    if (attrs) for (var k in attrs) {
      if (k === "class") n.className = attrs[k];
      else if (k === "html") n.innerHTML = attrs[k];
      else if (k === "text") n.textContent = attrs[k];
      else if (k.slice(0, 2) === "on" && typeof attrs[k] === "function") n.addEventListener(k.slice(2), attrs[k]);
      else if (attrs[k] != null && attrs[k] !== false) n.setAttribute(k, attrs[k]);
    }
    (kids || []).forEach(function (c) { if (c != null) n.appendChild(typeof c === "string" ? document.createTextNode(c) : c); });
    return n;
  }
  function getCookie(name) {
    var m = document.cookie.match("(?:^|; )" + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, "\\$1") + "=([^;]*)");
    return m ? decodeURIComponent(m[1]) : null;
  }
  function setCookie(name, value, maxAge) {
    var secure = (location.protocol === "https:") ? "; Secure" : "";
    document.cookie = name + "=" + encodeURIComponent(value) + "; path=/; max-age=" + maxAge + "; SameSite=Strict" + secure;
  }
  function randomToken() {
    try {
      var b = new Uint8Array(16); (window.crypto || window.msCrypto).getRandomValues(b);
      var o = ""; for (var i = 0; i < b.length; i++) o += ("0" + b[i].toString(16)).slice(-2); return o;
    } catch (e) { return String(Math.random()).slice(2) + String(Math.random()).slice(2); }
  }
  function validEmail(v) { v = (v || "").trim(); return v.length >= 5 && v.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }
  function money(sym, val) { return sym + Number(val).toFixed(2); }

  /* ---------- A/B variant ---------- */
  // Variant A = email-FIRST funnel (Page 1 email gate -> this selection page).
  // Variant B = this selection page is the ENTRY (choose first), then an email
  // gate opens just before checkout. Detected by the dedicated page handle or ?v=b.
  var GX_PARAMS = new URLSearchParams(location.search);
  var VARIANT = (/\/pages\/gift-select-first/i.test(location.pathname) || GX_PARAMS.get("v") === "b") ? "b" : "a";

  /* CSS named colors that cover most apparel swatch names; fall back to a hash. */
  var COLOR_MAP = {
    "black": "#111", "white": "#fff", "grey": "#9aa0a6", "gray": "#9aa0a6",
    "ultimate grey": "#9aa0a6", "grey sage": "#9caf88", "grey rabbit": "#b6b6ae",
    "navy": "#1f2a44", "limoges navy": "#26344f", "blue": "#2563eb", "azure blue": "#3a8dde",
    "pool blue": "#4cc3d9", "iceberg green": "#7fd1c1", "red": "#d1322c", "rio red": "#c8102e",
    "salsa": "#a01c2c", "viva magenta": "#bb2649", "cattleya orchid": "#a04e8f", "lilas": "#b9a3d6",
    "plum purple": "#5b2a86", "marigold": "#f4a300", "bright marigold": "#f4a300",
    "tangerine tango": "#e35336", "dubarry": "#e2607a", "cork": "#b07d52", "coffee beans": "#5b3a29",
    "rum raisin": "#6e3b3b", "green": "#1f9d55", "pink": "#ec4899", "purple": "#7c3aed",
    "orange": "#f97316", "yellow": "#facc15", "beige": "#e8dcc8", "brown": "#7c4a24", "teal": "#14b8a6"
  };
  function colorToCss(name) {
    if (!name) return "#cbd5e1";
    var key = String(name).trim().toLowerCase();
    if (COLOR_MAP[key]) return COLOR_MAP[key];
    // partial match (e.g. "...navy", "azure blue / xx")
    for (var k in COLOR_MAP) { if (key.indexOf(k) !== -1) return COLOR_MAP[k]; }
    // deterministic fallback hue from the string
    var h = 0; for (var i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) % 360;
    return "hsl(" + h + ",42%,55%)";
  }

  /* ---------- state ---------- */
  var DATA = null;
  var REVIEWS = null;   // product id -> [reviews], lazy-loaded from gift-reviews.json
  var MAX = 3;
  var SYM = "$";
  // selection: cardId -> { variantId, label, price, image, qty } (one variant per card, with quantity)
  var selection = {};
  var cardById = {};      // card.id -> built card object (so "Change" can re-open the picker)
  var GLOBAL_SWATCH = {}; // color-name(lower) -> {color,image}, harvested from ALL products' option swatches
  var activeSize = null;  // current size filter (null = show all)

  function totalQty() { var n = 0; for (var k in selection) n += selection[k].qty; return n; }
  // capacity used by every OTHER card, so a card can still grow its own qty up to the tier
  function qtyExcluding(cardId) { return totalQty() - (selection[cardId] ? selection[cardId].qty : 0); }

  /* ---------- size filter (shop-your-size bar) ---------- */
  var SIZE_ORDER = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "2XL", "3XL", "4XL"];
  function sizeRank(s) {
    var u = String(s).toUpperCase().trim();
    var i = SIZE_ORDER.indexOf(u); if (i !== -1) return i;
    var n = parseInt(u, 10); if (!isNaN(n)) return 100 + n; // numeric sizes after alpha
    return 999;
  }
  // Collapse dual-labelled sizes so the filter shows each size ONCE:
  //   "8 / S" -> "S",  "16 / 2XL" -> "2XL",  "S" -> "S"
  function normSize(label) {
    var s = String(label).trim();
    var m = s.match(/\/\s*([A-Za-z0-9]+)\s*$/);   // alpha part after a slash, if any
    return (m ? m[1] : s).toUpperCase();
  }
  function buildFilterBar(cards) {
    // group raw size labels by canonical key; DISPLAY the dual "N / ALPHA" label when present
    var byKey = {};   // canonical key (e.g. "S") -> display label (prefers the dual "8 / S" form)
    cards.forEach(function (c) {
      (c.sizes || []).forEach(function (lbl) {
        var key = normSize(lbl);
        if (!byKey[key] || (lbl.indexOf("/") !== -1 && byKey[key].indexOf("/") === -1)) byKey[key] = lbl;
      });
    });
    var keys = Object.keys(byKey).sort(function (a, b) { return sizeRank(a) - sizeRank(b) || (a < b ? -1 : 1); });
    if (keys.length < 2) return; // nothing meaningful to filter
    var wrap = document.getElementById("gx-filter-chips"); if (!wrap) return;
    wrap.innerHTML = "";
    var allChip = el("button", { "class": "gx-fchip is-active", type: "button", text: "All sizes", onclick: function () { applySizeFilter(null, allChip); } });
    wrap.appendChild(allChip);
    keys.forEach(function (key) {
      var ch = el("button", { "class": "gx-fchip", type: "button", text: byKey[key], onclick: function () { applySizeFilter(key, ch); } });
      wrap.appendChild(ch);
    });
    var fb = document.getElementById("gx-filter"); if (fb) fb.hidden = false;
  }
  function applySizeFilter(size, chip) {
    activeSize = size;
    var wrap = document.getElementById("gx-filter-chips");
    if (wrap) Array.prototype.forEach.call(wrap.children, function (x) { x.classList.remove("is-active"); });
    if (chip) chip.classList.add("is-active");
    var grid = document.getElementById("gx-grid"); if (!grid) return;
    var shown = 0;
    Array.prototype.forEach.call(grid.querySelectorAll(".gx-card"), function (node) {
      var sizes = (node.getAttribute("data-sizes") || "").split("|");
      var ok = !size || sizes.indexOf(size) !== -1;
      node.classList.toggle("is-filtered-out", !ok);
      if (ok) shown++;
    });
    var empty = grid.querySelector(".gx-empty-filter");
    if (!shown) {
      if (!empty) { empty = el("p", { "class": "gx-empty-filter" }); grid.appendChild(empty); }
      empty.textContent = "No free items in size " + size + " right now — try another size."; empty.style.display = "";
    } else if (empty) { empty.style.display = "none"; }
  }
  // colors that actually come in the currently-filtered size (all colors if no size filter)
  function colorsForActiveSize(card) {
    if (!activeSize || !card.hasColorRow) return card.colors;
    var f = card.colors.filter(function (c) {
      return (c.sizes || []).some(function (s) { return s.eligible && normSize(s.label) === activeSize; });
    });
    return f.length ? f : card.colors;
  }

  /* ---------- session guard ---------- */
  function guard() {
    var fallback = parseInt(($("#gx-root") || {}).getAttribute && $("#gx-root").getAttribute("data-default-tier"), 10);
    if (VARIANT === "b") {
      // Variant B: this page IS the funnel entry (no prior email/session). Tier
      // comes from the ?tier= ad param; mint the session + tier cookies so the
      // rest of the flow (and the post-email-gate checkout) behaves identically.
      var urlTier = parseInt(GX_PARAMS.get("tier"), 10);
      MAX = (urlTier >= 1 && urlTier <= 5) ? urlTier : ((fallback >= 1 && fallback <= 5) ? fallback : 3);
      if (!getCookie("nirlon_gift_session")) setCookie("nirlon_gift_session", randomToken(), 3600);
      setCookie("nirlon_gift_tier", String(MAX), 3600);
      return true;
    }
    var sess = getCookie("nirlon_gift_session");
    if (!sess) { window.location.replace("/"); return false; }
    var t = parseInt(getCookie("nirlon_gift_tier"), 10);
    MAX = (t >= 1 && t <= 5) ? t : ((fallback >= 1 && fallback <= 5) ? fallback : 3);
    return true;
  }

  /* ---------- data normalization ----------
   * Build a flat, render-friendly model per card:
   *   card.colors = [{ name, css, image, sizes:[{label, lengths:[{label, variant}]}] }]
   * A "variant" is a leaf with {id, eligible, price, image}.
   * Cards come in two shapes:
   *   A) native Color option (single product, color is a variant option)
   *   B) split family (many member products, color = member.color_group, only Size[/length])
   * Single-color products (Size only) -> one synthetic color = "" (no swatch row).
   */
  function roleOf(member, optName) {
    var match = (member.options || []).find(function (o) { return o.name === optName; });
    return match ? match.role : "other";
  }

  function buildCard(card, idx) {
    var id = "card-" + idx;
    var members = card.members || [];
    var hasNativeColor = members.length === 1 &&
      (members[0].options || []).some(function (o) { return o.role === "color"; });
    var isSplit = members.length > 1; // shared title across per-color products

    var colors = []; // {name, css, image, sizeMap}
    var colorIndex = {}; // name -> color obj

    function ensureColor(name, image, swatch) {
      var key = name || "__single__";
      if (!colorIndex[key]) {
        var c = { name: name || "", css: colorToCss(name), image: image || card.image,
                  swColor: swatch && swatch.color, swImage: swatch && swatch.image,
                  sizes: [], sizeIndex: {} };
        colorIndex[key] = c; colors.push(c);
      } else {
        if (image && !colorIndex[key].image) colorIndex[key].image = image;
        if (swatch && swatch.color && !colorIndex[key].swColor) colorIndex[key].swColor = swatch.color;
        if (swatch && swatch.image && !colorIndex[key].swImage) colorIndex[key].swImage = swatch.image;
      }
      return colorIndex[key];
    }
    function ensureSize(colorObj, label) {
      if (!colorObj.sizeIndex[label]) {
        var s = { label: label, lengths: [], lengthIndex: {} };
        colorObj.sizeIndex[label] = s; colorObj.sizes.push(s);
      }
      return colorObj.sizeIndex[label];
    }

    members.forEach(function (m) {
      // figure out which option name maps to which role for THIS member
      var nameByRole = {};
      (m.options || []).forEach(function (o) { nameByRole[o.role] = o.name; });
      (m.variants || []).forEach(function (v) {
        var opts = v.options || {};
        var colorName = isSplit ? (m.color_group || "")
          : (nameByRole.color ? opts[nameByRole.color] : "");
        var sizeLabel = nameByRole.size ? opts[nameByRole.size] : (v.title || "One size");
        var lengthName = nameByRole.length ? opts[nameByRole.length] : null;

        // Split sports-bras carry their swatch on the color-pattern metaobject (cp_*);
        // native-color products carry it on the option value (color_swatches). Scoped to
        // isSplit so the bra fix cannot affect the other products.
        var swatch = isSplit
          ? GLOBAL_SWATCH[(colorName || '').toLowerCase()]
          : (m.color_swatches && m.color_swatches[colorName]);
        var c = ensureColor(colorName, (m.color_images && m.color_images[colorName]) || m.image, swatch);
        var s = ensureSize(c, sizeLabel || "One size");
        var lengthKey = lengthName || "__none__";
        if (!s.lengthIndex[lengthKey]) {
          var leaf = {
            label: lengthName || null,
            // The island now emits ONLY gift-eligible + in-stock variants, so every
            // leaf is selectable.
            variant: { id: v.id, eligible: true, price: v.price, image: (m.color_images && m.color_images[colorName]) || m.image }
          };
          s.lengthIndex[lengthKey] = leaf; s.lengths.push(leaf);
        }
      });
    });

    // eligibility roll-ups
    colors.forEach(function (c) {
      c.eligible = false;
      c.sizes.forEach(function (s) {
        s.eligible = s.lengths.some(function (l) { return l.variant.eligible; });
        if (s.eligible) c.eligible = true;
      });
    });
    var anyEligible = colors.some(function (c) { return c.eligible; });
    // price for strikethrough = first variant price found
    var refPrice = "";
    outer: for (var ci = 0; ci < colors.length; ci++)
      for (var si = 0; si < colors[ci].sizes.length; si++)
        for (var li = 0; li < colors[ci].sizes[si].lengths.length; li++) { refPrice = colors[ci].sizes[si].lengths[li].variant.price; break outer; }

    // available size labels (eligible only)
    var sizeSet = {};
    colors.forEach(function (c) { c.sizes.forEach(function (s) { if (s.eligible) sizeSet[s.label] = true; }); });

    // FULL option runs (all values incl. ineligible) -> show all colors/sizes, cross out the rest
    var allSizes = [], allColors = [], seenS = {}, seenC = {};
    members.forEach(function (m) {
      (m.options || []).forEach(function (o) {
        if (o.role === "size") (o.values || []).forEach(function (v) { if (!seenS[v]) { seenS[v] = 1; allSizes.push(v); } });
        if (o.role === "color") (o.values || []).forEach(function (v) { if (!seenC[v]) { seenC[v] = 1; allColors.push(v); } });
      });
    });
    allSizes.sort(function (a, b) { return sizeRank(a) - sizeRank(b) || (a < b ? -1 : 1); });

    return {
      id: id, title: card.title, vendor: card.vendor, type: card.type,
      image: card.image, colors: colors, hasColorRow: hasNativeColor || isSplit,
      description: card.description || "", sizes: Object.keys(sizeSet),
      images: (card.images && card.images.length) ? card.images : (card.image ? [card.image] : []),
      hasLength: members.some(function (m) { return (m.options || []).some(function (o) { return o.role === "length"; }); }),
      anyEligible: anyEligible, refPrice: refPrice,
      rating: card.rating || 0, reviews: card.reviews || 0,
      productIds: members.map(function (m) { return m.id; }).filter(Boolean),
      allSizes: allSizes, allColors: allColors
    };
  }

  /* ---------- sub-brand badge from vendor/type/title ---------- */
  function badgeFor(card) {
    var hay = ((card.vendor || "") + " " + (card.type || "") + " " + (card.title || "")).toUpperCase();
    var brands = ["NIRLUX", "COTTONIR", "NIRJEANS", "NYLONIR", "FLASHINIR", "FLEECENIR"];
    for (var i = 0; i < brands.length; i++) if (hay.indexOf(brands[i]) !== -1) return cap(brands[i]);
    return null;
  }
  function cap(b) {
    var map = { NIRLUX: "NirLUX", COTTONIR: "CottoNIR", NIRJEANS: "NirJEANS", NYLONIR: "NyloNIR", FLASHINIR: "FlashiNIR", FLEECENIR: "FleeceNIR" };
    return map[b] || b;
  }

  /* ---------- rendering ---------- */
  // real Judge.me rating -> partial-fill star row (returns null if no reviews)
  function starRow(rating, count) {
    rating = Number(rating) || 0; count = Number(count) || 0;
    if (count < 1 || rating <= 0) return null;
    var pct = Math.max(0, Math.min(100, (rating / 5) * 100));
    var ic = el("span", { "class": "gx-stars-ic" });
    ic.appendChild(el("span", { "class": "gx-stars-bg", text: "★★★★★" }));
    var fg = el("span", { "class": "gx-stars-fg", text: "★★★★★" });
    fg.style.width = pct + "%";
    ic.appendChild(fg);
    var row = el("div", { "class": "gx-stars" });
    row.appendChild(ic);
    row.appendChild(el("span", { "class": "gx-stars-n", text: rating.toFixed(1) + " · " + count.toLocaleString() + " reviews" }));
    return row;
  }

  // per-product reviews (stars + text + real photos) for the quick-view modal
  function renderProductReviews(card) {
    if (!REVIEWS) return null;
    var list = [];
    (card.productIds || []).forEach(function (pid) {
      (REVIEWS[String(pid)] || []).forEach(function (r) { list.push(r); });
    });
    if (!list.length) {
      // rating exists but no fetched review text -> short summary instead of an empty section
      if (Number(card.reviews) > 0 && Number(card.rating) > 0) {
        var s = el("div");
        s.appendChild(el("h4", { "class": "gx-m__rv-h", text: "Customer reviews" }));
        var sr2 = starRow(card.rating, card.reviews);
        if (sr2) s.appendChild(sr2);
        s.appendChild(el("p", { "class": "gx-m__rv-body", style: "margin-top:6px",
          text: "Rated " + Number(card.rating).toFixed(1) + " out of 5 by " + Number(card.reviews).toLocaleString() + " verified buyers." }));
        return s;
      }
      return null;
    }
    list.sort(function (a, b) { return (b.r || 0) - (a.r || 0); });   // high ratings first; photo+text mix
    var sec = el("div");
    var totalCount = Number(card.reviews) || list.length;
    sec.appendChild(el("h4", { "class": "gx-m__rv-h", text: "Customer reviews (" + totalCount.toLocaleString() + ")" }));
    var wrap = el("div", { "class": "gx-m__rv-list" });
    list.slice(0, 30).forEach(function (r) {
      var n = Math.max(1, Math.min(5, r.r || 5));
      var item = el("div", { "class": "gx-m__rv" });
      item.appendChild(el("div", { "class": "gx-rv-stars", text: "★★★★★".slice(0, n) + "☆☆☆☆☆".slice(0, 5 - n) }));
      item.appendChild(el("p", { "class": "gx-m__rv-body", text: r.b || "" }));
      item.appendChild(el("span", { "class": "gx-m__rv-by", text: (r.n ? r.n + " · " : "") + "Verified Buyer" }));
      if (r.p && r.p.length) {
        var pics = el("div", { "class": "gx-m__rv-pics" });
        r.p.forEach(function (u) {
          var im = el("img", { src: u, alt: "customer photo", loading: "lazy" });
          im.addEventListener("click", function () { openZoom(u); });
          pics.appendChild(im);
        });
        item.appendChild(pics);
      }
      wrap.appendChild(item);
    });
    sec.appendChild(wrap);
    return sec;
  }

  function renderCard(card) {
    var node = el("div", { "class": "gx-card" + (card.anyEligible ? "" : " is-oos"), "data-card": card.id, "data-sizes": (card.sizes || []).map(normSize).join("|") });

    // thumb (whole image is clickable -> opens the picker; Clarity: users dead-clicked the image)
    var thumb = el("div", {
      "class": "gx-thumb",
      style: card.anyEligible ? "cursor:pointer" : null,
      onclick: card.anyEligible ? function () { openPicker(card, node); } : null
    }, [
      card.image ? el("img", { src: card.image, alt: card.title, loading: "lazy" }) : null
    ]);
    var badge = badgeFor(card);
    if (badge) thumb.appendChild(el("span", { "class": "gx-badge", text: badge }));
    thumb.appendChild(el("div", { "class": "gx-check", html: "&#10003;" }));
    if (!card.anyEligible) thumb.appendChild(el("div", { "class": "gx-oosbanner", text: "Out of Stock" }));
    node.appendChild(thumb);

    // body
    var body = el("div", { "class": "gx-body" });
    body.appendChild(el("h3", {
      "class": "gx-title", text: card.title,
      style: card.anyEligible ? "cursor:pointer" : null,
      onclick: card.anyEligible ? function () { openPicker(card, node); } : null
    }));
    var sr = starRow(card.rating, card.reviews); if (sr) body.appendChild(sr);
    body.appendChild(el("div", { "class": "gx-price" }, [
      el("s", { text: card.refPrice || "" }),
      el("b", { text: money(SYM, 0) })
    ]));

    // selected summary line — populated by syncSelectedLines (label + qty stepper + change/remove)
    body.appendChild(el("div", { "class": "gx-selected-line" }));

    // primary CTA
    var cta = el("button", {
      "class": "gx-cta", type: "button",
      text: card.anyEligible ? "Select Size & Color" : "Out of Stock",
      onclick: function () { openPicker(card, node); }
    });
    if (!card.anyEligible) cta.disabled = true;
    body.appendChild(cta);

    // picker (built lazily on open, but container exists)
    body.appendChild(el("div", { "class": "gx-picker", "data-picker": card.id }));
    node.appendChild(body);

    return node;
  }

  /* ---------- quick-view modal ---------- */
  function openModal() {
    var m = $("#gx-modal");
    if (m) {
      m.classList.add("is-open"); document.documentElement.style.overflow = "hidden";
      var p = $(".gx-m__panel", m); if (p) p.scrollTop = 0;            // open at the top
      var tt = $("#gx-m-totop"); if (tt) tt.classList.remove("show");
    }
  }
  function closeModal() {
    var m = $("#gx-modal"); if (m) { m.classList.remove("is-open"); document.documentElement.style.overflow = ""; }
  }
  function openZoom(src) { var z = $("#gx-zoom"); if (!z) return; var zi = $("img", z); if (zi) zi.src = src; z.classList.add("is-open"); }
  function closeZoom() { var z = $("#gx-zoom"); if (z) z.classList.remove("is-open"); }
  function openSizeChart() { var s = $("#gx-sizechart"); if (s) s.classList.add("is-open"); }
  function closeSizeChart() { var s = $("#gx-sizechart"); if (s) s.classList.remove("is-open"); }
  // Flip WIDE size tables (sizes across the top) to VERTICAL (sizes down the rows) so
  // they fit the popup without horizontal scroll. Runs once at boot on the page content.
  function transposeSizeChart() {
    var body = $("#gx-sizechart .gx-sc__body"); if (!body) return;
    Array.prototype.forEach.call(body.querySelectorAll("table"), function (table) {
      try {
        var rows = Array.prototype.slice.call(table.querySelectorAll("tr"));
        var matrix = rows.map(function (tr) {
          return Array.prototype.slice.call(tr.querySelectorAll("th,td")).map(function (c) { return c.textContent.replace(/\s+/g, " ").trim(); });
        });
        if (matrix.length < 2) return;
        var cols = matrix[0].length;
        if (cols <= matrix.length) return;            // only flip tables that are wider than tall
        var tnew = document.createElement("table");
        for (var i = 0; i < cols; i++) {              // i = new row (was a column)
          var tr = document.createElement("tr");
          for (var j = 0; j < matrix.length; j++) {   // j = new col (was a row)
            var isHead = (i === 0 || j === 0);
            var cell = document.createElement(isHead ? "th" : "td");
            cell.textContent = (matrix[j] && matrix[j][i] != null) ? matrix[j][i] : "";
            tr.appendChild(cell);
          }
          tnew.appendChild(tr);
        }
        table.parentNode.replaceChild(tnew, table);
      } catch (e) {}
    });
  }
  function populateModalHeader(card) {
    var t = $("#gx-modal-title"); if (t) t.textContent = card.title;
    var oldStars = $("#gx-modal-stars"); if (oldStars) oldStars.remove();
    var msr = starRow(card.rating, card.reviews);
    if (msr && t) { msr.id = "gx-modal-stars"; t.parentNode.insertBefore(msr, t.nextSibling); }
    var rvBox = $("#gx-modal-reviews");
    if (rvBox) { rvBox.innerHTML = ""; var rl = renderProductReviews(card); if (rl) rvBox.appendChild(rl); }
    var pr = $("#gx-modal-price");
    if (pr) { pr.innerHTML = ""; pr.appendChild(el("s", { text: card.refPrice || "" })); pr.appendChild(el("b", { text: "FREE" })); }
    var d = $("#gx-modal-desc"); if (d) { d.textContent = card.description || ""; d.classList.remove("is-expanded"); }
    // meta row: expandable-description toggle + size-chart popup button
    var meta = $("#gx-modal-meta");
    if (meta) {
      meta.innerHTML = "";
      if ((card.description || "").length > 140 && d) {
        var more = el("button", { "class": "gx-m__more", type: "button", text: "Read more" });
        more.addEventListener("click", function () { var ex = d.classList.toggle("is-expanded"); more.textContent = ex ? "Read less" : "Read more"; });
        meta.appendChild(more);
      }
      if ($("#gx-sizechart")) {
        var scBtn = el("button", { "class": "gx-m__sizechart", type: "button", text: "📏 Size chart" });
        scBtn.addEventListener("click", openSizeChart);
        meta.appendChild(scBtn);
      }
    }
    var g = $("#gx-modal-gallery"); if (!g) return;
    g.innerHTML = "";
    var imgs = (card.images && card.images.length) ? card.images : (card.image ? [card.image] : []);
    var mainImg = imgs[0] ? el("img", { src: imgs[0], alt: card.title }) : null;
    var main = el("div", { "class": "gx-m-main" }, [mainImg]);
    if (mainImg) main.addEventListener("click", function () { openZoom(mainImg.src); });
    g.appendChild(main);
    if (imgs.length > 1) {
      var thumbs = el("div", { "class": "gx-m-thumbs" });
      imgs.forEach(function (src, i) {
        var th = el("img", { src: src, alt: "", "class": i === 0 ? "is-active" : "" });
        th.addEventListener("click", function () {
          if (mainImg) mainImg.src = src;
          Array.prototype.forEach.call(thumbs.children, function (x) { x.classList.remove("is-active"); });
          th.classList.add("is-active");
        });
        thumbs.appendChild(th);
      });
      g.appendChild(thumbs);
    }
  }

  // build the picker INTO the modal (gallery + description rendered above it)
  function openPicker(card, node) {
    if (!card.anyEligible) return;
    populateModalHeader(card);
    var picker = $("#gx-modal-picker");
    picker.innerHTML = "";

    var choice = { color: null, size: null, length: null };

    var colorWrap = el("div", { "class": "gx-opt gx-opt-color" });
    var sizeWrap = el("div", { "class": "gx-opt gx-opt-size" });
    var lenWrap = el("div", { "class": "gx-opt gx-opt-length" });
    var hint = el("p", { "class": "gx-hint" });
    var pcount = el("div", { "class": "gx-picker-count" });   // "pick up to N — X added" (multi-select hint)
    var actions = el("div", { "class": "gx-picker-actions" });
    var addBtn = el("button", { "class": "gx-add", type: "button", text: "Add to Gift Bag", disabled: "disabled" });
    var cancelBtn = el("button", { "class": "gx-cancel", type: "button", text: "Cancel", onclick: function () { closeModal(); } });
    actions.appendChild(addBtn); actions.appendChild(cancelBtn);

    function resolveLeaf() {
      var c = choice.color; if (!c) return null;
      var s = (c.sizes || []).find(function (x) { return x.label === (choice.size && choice.size.label); });
      if (!s) return null;
      // if this size has labeled inseam/length options, REQUIRE a length pick (auto-set
      // for a single option in buildLengths) so the customer always sees & confirms it.
      if (card.hasLength && s.lengths.length && s.lengths[0].label) {
        return (choice.length && choice.length.variant) ? choice.length : null;
      }
      var avail = s.lengths.filter(function (l) { return l.variant.eligible; });
      return avail.length ? avail[0] : (s.lengths[0] || null);
    }
    function refreshAdd() {
      var leaf = resolveLeaf();
      var full = qtyExcluding(card.id) >= MAX;   // no room for even one piece from this card
      addBtn.disabled = !(leaf && leaf.variant && leaf.variant.eligible) || full;
      hint.textContent = "";
      hint.classList.remove("gx-limit");
      if (full) { hint.textContent = "You've reached your limit of " + MAX + " pieces. Remove one to swap."; hint.classList.add("gx-limit"); }
      var used = totalQty();
      pcount.textContent = "You can pick up to " + MAX + " free pieces — " + used + " of " + MAX + " added.";
    }

    /* ---- length builder ---- */
    function buildLengths(sizeObj) {
      lenWrap.innerHTML = "";
      choice.length = null;
      // Show the inseam/length row whenever this product has the option AND this size has
      // labeled lengths — INCLUDING a single one, so the customer SEES which inseam they get.
      if (!card.hasLength || !sizeObj || !sizeObj.lengths.length || !sizeObj.lengths[0].label) { refreshAdd(); return; }
      lenWrap.appendChild(el("h4", { html: "Length <span class='gx-optval'></span>" }));
      var chips = el("div", { "class": "gx-chips" });
      sizeObj.lengths.forEach(function (l) {
        var b = el("button", { "class": "gx-chip", type: "button", text: l.label });
        if (!l.variant.eligible) { b.disabled = true; b.title = "Out of stock"; }
        else b.addEventListener("click", function () {
          choice.length = l;
          Array.prototype.forEach.call(chips.children, function (x) { x.classList.remove("is-active"); });
          b.classList.add("is-active");
          $(".gx-optval", lenWrap).textContent = l.label;
          refreshAdd();
        });
        chips.appendChild(b);
      });
      lenWrap.appendChild(chips);
      // exactly one selectable length -> auto-select it (still visible to the customer)
      var sel = sizeObj.lengths.filter(function (l) { return l.variant.eligible; });
      if (sel.length === 1) {
        choice.length = sel[0];
        Array.prototype.forEach.call(chips.children, function (x) { if (x.textContent === sel[0].label) x.classList.add("is-active"); });
        $(".gx-optval", lenWrap).textContent = sel[0].label;
      }
      refreshAdd();
    }

    /* ---- size builder ---- */
    function buildSizes(colorObj) {
      sizeWrap.innerHTML = ""; lenWrap.innerHTML = "";
      choice.size = null; choice.length = null;
      sizeWrap.appendChild(el("h4", { html: "Size <span class='gx-optval'></span>" }));
      var chips = el("div", { "class": "gx-chips" });
      var bySize = {}; (colorObj.sizes || []).forEach(function (s) { bySize[s.label] = s; });
      var sizeRun = (card.allSizes && card.allSizes.length) ? card.allSizes : (colorObj.sizes || []).map(function (s) { return s.label; });
      sizeRun.forEach(function (label) {
        var s = bySize[label];
        var b = el("button", { "class": "gx-chip", type: "button", text: label });
        if (!s || !s.eligible) { b.disabled = true; b.title = "Not in this offer"; }
        else b.addEventListener("click", function () {
          choice.size = s;
          Array.prototype.forEach.call(chips.children, function (x) { x.classList.remove("is-active"); });
          b.classList.add("is-active");
          $(".gx-optval", sizeWrap).textContent = s.label;
          buildLengths(s);
          refreshAdd();
        });
        chips.appendChild(b);
      });
      sizeWrap.appendChild(chips);
      // auto-pick: the active size filter if available here, else the only size — fewer taps
      var eligSizes = (colorObj.sizes || []).filter(function (s) { return s.eligible; });
      var pre = null;
      if (activeSize) pre = eligSizes.filter(function (s) { return normSize(s.label) === activeSize; })[0];
      if (!pre && eligSizes.length === 1) pre = eligSizes[0];
      if (pre) {
        var pchip = Array.prototype.slice.call(chips.children).filter(function (x) { return x.textContent === pre.label && !x.disabled; })[0];
        if (pchip) pchip.click();
      }
      refreshAdd();
    }

    /* ---- color builder (swatches) ---- */
    // with a size filter on, only show colors that actually come in that size
    var colorList = colorsForActiveSize(card);
    if (card.hasColorRow) {
      colorWrap.appendChild(el("h4", { html: "Color <span class='gx-optval'></span>" }));
      var sw = el("div", { "class": "gx-swatches" });
      var byColor = {}; colorList.forEach(function (c) { byColor[(c.name || "").toLowerCase()] = c; });
      var colorRun = (card.allColors && card.allColors.length) ? card.allColors : colorList.map(function (c) { return c.name; });
      colorRun.forEach(function (cname) {
        var c = byColor[(cname || "").toLowerCase()];
        var elig = !!(c && c.eligible);
        var gsw = GLOBAL_SWATCH[(cname || "").toLowerCase()] || {};
        var swImage = c ? c.swImage : gsw.image;
        var swColor = c ? c.swColor : gsw.color;
        var b = el("button", {
          "class": "gx-swatch", type: "button",
          title: cname + (elig ? "" : " — not in this offer"), "aria-label": cname,
          style: swImage ? ("background-image:url('" + swImage + "');background-size:cover;background-position:center")
                         : ("background:" + (swColor || colorToCss(cname)))
        });
        if (!elig) b.disabled = true;
        else b.addEventListener("click", function () {
          choice.color = c;
          Array.prototype.forEach.call(sw.children, function (x) { x.classList.remove("is-active"); });
          b.classList.add("is-active");
          $(".gx-optval", colorWrap).textContent = c.name;
          var img = $(".gx-thumb img", node);
          if (img && c.image) img.src = c.image;
          var mimg = $("#gx-modal-gallery .gx-m-main img");
          if (mimg && c.image) mimg.src = c.image;
          buildSizes(c);
        });
        sw.appendChild(b);
      });
      colorWrap.appendChild(sw);
      picker.appendChild(colorWrap);
    } else {
      // no color option at all -> auto-select the implicit single color
      choice.color = card.colors[0];
    }

    picker.appendChild(sizeWrap);
    picker.appendChild(lenWrap);
    picker.appendChild(hint);
    picker.appendChild(pcount);
    picker.appendChild(actions);

    // a single available color (incl. size-filtered to one) is still SHOWN, then auto-picked
    if (card.hasColorRow && colorList.length === 1) {
      var onlySw = colorWrap.querySelector(".gx-swatch:not([disabled])");
      if (onlySw) onlySw.click();
    } else if (!card.hasColorRow && card.colors[0]) {
      buildSizes(card.colors[0]);
    }

    openModal();

    addBtn.addEventListener("click", function () {
      var leaf = resolveLeaf();
      if (!leaf || !leaf.variant || !leaf.variant.eligible) return;
      if (qtyExcluding(card.id) >= MAX) return;   // bag is full
      var parts = [];
      if (card.hasColorRow && choice.color && choice.color.name) parts.push(choice.color.name);
      if (choice.size) parts.push(choice.size.label);
      if (leaf.label) parts.push(leaf.label);
      // preserve this card's prior quantity when re-picking (capped to remaining room), else 1
      var prevQty = selection[card.id] ? selection[card.id].qty : 0;
      var qty = Math.max(1, Math.min(prevQty || 1, MAX - qtyExcluding(card.id)));
      selection[card.id] = {
        variantId: leaf.variant.id,
        label: parts.join(" / ") || card.title,
        price: leaf.variant.price,
        image: leaf.variant.image || card.image,
        qty: qty
      };
      closeModal();
      node.classList.add("is-selected");
      updateTotals();
    });

    refreshAdd();
  }

  function removeSelection(cardId) {
    delete selection[cardId];
    var node = document.querySelector('.gx-card[data-card="' + cardId + '"]');
    if (node) node.classList.remove("is-selected", "is-open");
    updateTotals();
  }
  function incQty(cardId) {
    if (selection[cardId] && totalQty() < MAX) { selection[cardId].qty++; updateTotals(); }
  }
  function decQty(cardId) {
    if (!selection[cardId]) return;
    selection[cardId].qty--;
    if (selection[cardId].qty <= 0) delete selection[cardId];
    updateTotals();
  }

  /* ---------- totals / limits / shipping ---------- */
  function updateTotals() {
    var n = totalQty();
    $("#gx-count").textContent = n;
    $("#gx-max").textContent = MAX;
    $("#gx-foot-count").textContent = n;
    $("#gx-foot-label").textContent = n === 1 ? "piece selected" : "pieces selected";
    var pct = Math.min(100, Math.round((n / MAX) * 100));
    $("#gx-progress-bar").style.width = pct + "%";

    var shipTxt = n === 0 ? "—" : money(SYM, SHIP_PRICE[Math.min(n, 5)]);
    $("#gx-ship").textContent = shipTxt;
    $("#gx-foot-ship").textContent = shipTxt;

    // "You're saving $X" — total retail value of the free gear, so the deal feels huge
    var saved = 0;
    for (var sid in selection) {
      var sc = cardById[sid];
      var rp = sc ? parseFloat(String(sc.refPrice).replace(/[^0-9.]/g, "")) : 0;
      if (!isNaN(rp)) saved += rp * selection[sid].qty;
    }
    var savedTxt = money(SYM, saved);   // exact total, not rounded (e.g. $89.97)
    var sw = $("#gx-savewrap"); if (sw) sw.hidden = (n === 0);
    var sv = $("#gx-saved"); if (sv) sv.textContent = savedTxt;
    var fsw = $("#gx-foot-savewrap"); if (fsw) fsw.hidden = (n === 0);
    var fsv = $("#gx-foot-saved"); if (fsv) fsv.textContent = savedTxt;

    var full = n >= MAX;
    // when the bag is full, block NEW picks on unselected cards. Selected cards can still
    // be re-opened (Change) and their +/- steppers manage quantity.
    Array.prototype.forEach.call(document.querySelectorAll(".gx-card"), function (node) {
      var id = node.getAttribute("data-card");
      var isSel = !!selection[id];
      var cta = $(".gx-cta", node);
      if (cta && !cta.classList.contains("disabled-oos")) {
        var oos = node.classList.contains("is-oos");
        cta.disabled = oos || (full && !isSel);
        cta.title = (full && !isSel && !oos) ? "You've reached your limit" : "";
      }
    });

    $("#gx-checkout").disabled = n < 1;
    syncSelectedLines();
  }

  function syncSelectedLines() {
    Array.prototype.forEach.call(document.querySelectorAll(".gx-card"), function (node) {
      var id = node.getAttribute("data-card");
      var line = $(".gx-selected-line", node);
      if (!line) return;
      var sel = selection[id];
      if (!sel) { line.innerHTML = ""; node.classList.remove("is-selected"); return; }
      node.classList.add("is-selected");
      line.innerHTML = "";
      line.appendChild(el("span", { "class": "gx-selected-name", text: sel.label }));
      line.appendChild(el("div", { "class": "gx-qtywrap" }, [
        el("button", { "class": "gx-qbtn", type: "button", text: "−", "aria-label": "decrease quantity", onclick: function () { decQty(id); } }),
        el("span", { "class": "gx-qn", text: String(sel.qty) }),
        el("button", { "class": "gx-qbtn", type: "button", text: "+", "aria-label": "increase quantity", disabled: (totalQty() >= MAX) ? "disabled" : null, onclick: function () { incQty(id); } })
      ]));
      line.appendChild(el("button", { "class": "gx-change", type: "button", text: "Change", onclick: function () { if (cardById[id]) openPicker(cardById[id], node); } }));
      line.appendChild(el("button", { "class": "gx-change gx-remove", type: "button", text: "Remove", onclick: function () { removeSelection(id); } }));
    });
  }

  /* ---------- toast ---------- */
  var toastTimer;
  function toast(msg) {
    var t = $("#gx-toast"); t.textContent = msg; t.classList.add("show");
    clearTimeout(toastTimer); toastTimer = setTimeout(function () { t.classList.remove("show"); }, 2600);
  }

  /* ---------- checkout ---------- */
  function postJSON(url, body) {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify(body || {})
    }).then(function (r) { return r.ok ? r.json().catch(function () { return {}; }) : r.json().then(function (j) { throw j; }); });
  }

  function resolveShipVariantId(count) {
    var n = Math.min(Math.max(count, 1), 5);
    var ids = (DATA.shipping_variant_ids || {});
    var id = parseInt(ids[String(n)], 10);
    return id > 0 ? id : null; // null -> fall back to SKU add
  }

  function checkout() {
    var n = totalQty();
    if (n < 1) return;
    var btn = $("#gx-checkout");
    btn.disabled = true; btn.classList.add("is-busy"); btn.textContent = "Preparing…";

    var giftItems = Object.keys(selection).map(function (k) { return { id: selection[k].variantId, quantity: selection[k].qty }; });
    var shipN = Math.min(n, 5);
    var shipId = resolveShipVariantId(shipN);
    var shipSku = "GIFT-SHIP-" + shipN;

    // 1) clear any existing cart (mixed-cart guard), 2) add gift items,
    // 3) add the shipping fee line, 4) redirect applying the free-shipping code
    //    (the free-gift Buy-X-Get-Y discount is automatic, so it needs no code).
    var addItems = giftItems.slice();
    if (shipId) addItems.push({ id: shipId, quantity: 1 });

    fetch("/cart/clear.js", { method: "POST", headers: { "Accept": "application/json" } })
      .then(function () { return postJSON("/cart/add.js", { items: addItems }); })
      // Stamp the A/B variant + tier on the cart so the resulting ORDER is
      // attributable (visible on the order; backup to per-ad-set Meta reporting).
      .then(function () { return postJSON("/cart/update.js", { attributes: { gift_variant: VARIANT, gift_tier: String(MAX) } }); })
      .then(function () {
        // If we didn't have a numeric shipping variant id, try adding by handle? Not possible via SKU directly.
        // Fall back: attempt /cart/add.js using the shipping variant id only if known; otherwise warn.
        if (!shipId) {
          // Shopify /cart/add.js needs a variant id, not a SKU. Without a configured id we cannot add the fee.
          // Proceed to checkout WITHOUT the fee line is unacceptable -> abort with guidance.
          throw { __noShip: true };
        }
      })
      .then(function () { redirectToCheckout(); })
      .catch(function (err) {
        if (err && err.__noShip) {
          toast("Shipping fee product not configured — contact support.");
        } else {
          var msg = (err && (err.description || err.message)) || "Something went wrong. Please try again.";
          toast(msg);
        }
        btn.disabled = false; btn.classList.remove("is-busy"); btn.textContent = "Go to Checkout";
      });
  }

  function redirectToCheckout() {
    // ONE code only. Shopify's one-step checkout no longer reliably auto-applies
    // multiple comma-separated codes from a URL, so the free GIFTS come from an
    // AUTOMATIC Buy-X-Get-Y discount (no code — it fires when the cart holds a
    // GIFT-SHIP fee + gift items), and here we apply ONLY the free-shipping code via
    // the single-code discount permalink (reliable on one-step checkout). The cart we
    // built above persists across the redirect, so at /checkout the customer sees:
    // gift items $0 (auto BxGy) + Shopify shipping $0 (GIFTSHIPFREE) -> they pay only
    // the GIFT-SHIP fee line.
    var code = (DATA.shipping_discount_code || "GIFTSHIPFREE").replace(/\s+/g, "");
    window.location.href = "/discount/" + encodeURIComponent(code) + "?redirect=/checkout";
  }

  /* ---------- Variant B: pre-checkout email gate ---------- */
  function openEmailGate() {
    if (totalQty() < 1) return;
    var m = $("#gx-emailgate");
    if (!m) { checkout(); return; }            // fail-open: never trap the user
    m.hidden = false; m.classList.add("is-open");
    var inp = $("#gx-eg-email"); if (inp) inp.focus();
  }
  function closeEmailGate() {
    var m = $("#gx-emailgate"); if (m) { m.classList.remove("is-open"); m.hidden = true; }
  }
  // The gate's form IS a native {% form 'customer' %} that posts (with Shopify's
  // reCAPTCHA token) into a hidden iframe -> creates the tagged gift-lead customer,
  // app-independent. On a valid submit we let that POST run, then continue to
  // checkout when the iframe lands (or after a safety timeout).
  function wireEmailGate() {
    var form = $("#gx-eg-form"), sink = $("#gx-eg-sink"), tags = $("#gx-eg-tags"),
        email = $("#gx-eg-email"), consent = $("#gx-eg-consent"), err = $("#gx-eg-error"),
        cta = $("#gx-eg-cta"), closeBtn = $("#gx-eg-close");
    if (!form) return;
    var submitted = false, forwarded = false;
    function forward() { if (forwarded) return; forwarded = true; closeEmailGate(); checkout(); }
    function refresh() { if (cta) cta.disabled = !(validEmail(email.value) && consent.checked); }
    email.addEventListener("input", function () { if (err) err.classList.remove("is-shown"); refresh(); });
    consent.addEventListener("change", refresh);
    if (closeBtn) closeBtn.addEventListener("click", closeEmailGate);
    if (sink) sink.addEventListener("load", function () { if (submitted) forward(); });
    refresh();
    form.addEventListener("submit", function (evt) {
      if (!validEmail(email.value) || !consent.checked) {
        evt.preventDefault();
        if (err) err.classList.add("is-shown");
        return;
      }
      submitted = true;
      if (cta) { cta.disabled = true; cta.textContent = "Claiming…"; }
      if (tags) tags.value = "gift-lead, gift-tier-" + MAX + ", gift-var-b";
      if (!getCookie("nirlon_gift_session")) setCookie("nirlon_gift_session", randomToken(), 3600);
      setCookie("nirlon_gift_tier", String(MAX), 3600);
      setTimeout(forward, 4500);   // safety net if the iframe load never fires
      // NO preventDefault -> the native customer form submits into #gx-eg-sink.
    });
  }

  /* ---------- boot ---------- */
  function boot() {
    if (!guard()) return;

    var raw = $("#gift-data");
    try { DATA = JSON.parse(raw.textContent); }
    catch (e) { console.error("[gift] bad data island", e); window.location.replace("/"); return; }
    SYM = DATA.currency || "$";
    if (DATA.reviews_url) { fetch(DATA.reviews_url).then(function (r) { return r.json(); }).then(function (j) { REVIEWS = j; }).catch(function () {}); }

    // Harvest a global color-name -> exact hex map from every product's option swatches,
    // so a color that has a real swatch on one product (the leggings) lends that exact
    // hex to products carrying only the color NAME (the split sports-bras) instead of
    // the approximate name map.
    (DATA.products || []).forEach(function (p) {
      var cs = p.color_swatches || {};
      for (var nm in cs) { if (cs[nm] && (cs[nm].color || cs[nm].image)) GLOBAL_SWATCH[nm.toLowerCase()] = cs[nm]; }
    });

    // The island is a FLAT product list; group by normalized title into cards
    // (split bra families share a title -> one card with several color members).
    var byTitle = {}, order = [];
    (DATA.products || []).forEach(function (p) {
      var key = (p.title || "").trim().toLowerCase();
      if (!byTitle[key]) {
        byTitle[key] = { title: p.title, vendor: p.vendor, type: p.type, image: p.image,
                         description: p.description || "", rating: p.rating || 0, reviews: p.reviews || 0,
                         images: (p.images && p.images.length ? p.images.slice() : []), members: [] };
        order.push(key);
      }
      byTitle[key].members.push({
        id: p.id, product_handle: p.handle, image: p.image, color_group: p.color_group,
        color_swatches: p.color_swatches, color_images: p.color_images,
        options: p.options, variants: p.variants
      });
    });
    var cards = order.map(function (k, i) { return buildCard(byTitle[k], i); })
      .filter(function (c) { return c.anyEligible; });   // drop any all-sold-out cards

    var grid = $("#gx-grid");
    if (!cards.length) {
      grid.appendChild(el("p", { "class": "gx-hint", text: "No gift items are available right now. Please check back soon." }));
    } else {
      // eligible cards first, OOS last
      cards.sort(function (a, b) { return (a.anyEligible === b.anyEligible) ? 0 : (a.anyEligible ? -1 : 1); });
      cards.forEach(function (c) { cardById[c.id] = c; grid.appendChild(renderCard(c)); });
    }

    $("#gx-max").textContent = MAX;
    if (VARIANT === "b") {
      wireEmailGate();
      $("#gx-checkout").addEventListener("click", openEmailGate);
    } else {
      $("#gx-checkout").addEventListener("click", checkout);
    }

    // overlay close handlers: quick-view modal, zoom lightbox, size-chart popup
    var _mx = $("#gx-modal-x"), _mb = $("#gx-modal-close");
    if (_mx) _mx.addEventListener("click", closeModal);
    if (_mb) _mb.addEventListener("click", closeModal);
    var _mp = $("#gx-modal .gx-m__panel"), _mt = $("#gx-m-totop");
    if (_mp && _mt) {
      _mp.addEventListener("scroll", function () { _mt.classList.toggle("show", _mp.scrollTop > 280); }, { passive: true });
      _mt.addEventListener("click", function () { _mp.scrollTo({ top: 0, behavior: "smooth" }); });
    }
    var _zoom = $("#gx-zoom"); if (_zoom) _zoom.addEventListener("click", closeZoom);
    var _scx = $("#gx-sc-x"), _scb = $("#gx-sc-close");
    if (_scx) _scx.addEventListener("click", closeSizeChart);
    if (_scb) _scb.addEventListener("click", closeSizeChart);
    transposeSizeChart();
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape") return;
      if ($("#gx-sizechart") && $("#gx-sizechart").classList.contains("is-open")) closeSizeChart();
      else if ($("#gx-zoom") && $("#gx-zoom").classList.contains("is-open")) closeZoom();
      else closeModal();
    });

    // reveal — force display:none on the loader: a CSS `display` rule on .gx-loading
    // overrides the [hidden] attribute, leaving the spinner visible. Set it inline.
    var _ld = $("#gx-loading"); if (_ld) { _ld.hidden = true; _ld.style.setProperty("display", "none", "important"); }
    $("#gx-app").hidden = false;
    $("#gx-foot").hidden = false;
    updateTotals();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
